import { useCallback, useEffect, useRef } from "react";
import type { AdminDocumentService } from "@/services/docuseal";
import { templateFromEvent } from "@/pages/admin/useBuilderBridge";

/** How long after the page is left a still-loading builder is watched for its draft. */
export const LATE_DRAFT_WATCH_MS = 60_000;

const draftIdFromEvent = (event: Event): number | null => {
  const id = templateFromEvent(event)?.id;
  return typeof id === "number" ? id : null;
};

/**
 * The page is gone but the builder's request that creates the template is not. Its
 * `load` still fires on the detached element (the React wrapper's own listeners are
 * already removed), so the draft is deleted when it arrives.
 */
function deleteDraftWhenItLoads(element: HTMLElement, service: AdminDocumentService) {
  const onLoad = (event: Event) => {
    clearTimeout(timer);
    const id = draftIdFromEvent(event);
    if (id !== null) void service.deleteRemoteTemplate(id).catch(() => {});
  };
  const timer = setTimeout(() => element.removeEventListener("load", onLoad), LATE_DRAFT_WATCH_MS);
  element.addEventListener("load", onLoad, { once: true });
}

interface DraftTemplateOptions {
  /** After the draft is deleted on leaving the page, so lists can refresh. */
  onDiscarded?: () => void;
}

export interface DraftTemplate {
  /** The id the builder created, once it has loaded. */
  id: () => number | null;
  /**
   * The admin saved: the draft must survive. Call before the save's first await.
   * Returns false when a save already holds it, so a repeated event is dropped.
   */
  keep: () => boolean;
  /** A save did not go through: the draft is discardable again. */
  release: () => void;
  /**
   * Deletes the draft the builder created. Safe to call repeatedly, from Cancel and
   * from leaving the page alike: one delete is shared and a success is remembered.
   * Rejects when the delete fails, and a later call retries.
   */
  discard: () => Promise<void>;
}

/**
 * The builder creates a new template as soon as it loads. Until the admin saves it, it
 * is a draft this page must delete again on Cancel and on leaving the page. Only the
 * id the builder reported for the template this page asked it to create is ever
 * deleted, never one picked from a list.
 */
export function useDraftTemplate(
  element: HTMLElement | null,
  /** The scope the builder created the draft in. */
  service: AdminDocumentService,
  { onDiscarded }: DraftTemplateOptions = {}
): DraftTemplate {
  const idRef = useRef<number | null>(null);
  const keepingRef = useRef(false);
  const deletionRef = useRef<Promise<void> | null>(null);
  const elementRef = useRef(element);
  const onDiscardedRef = useRef(onDiscarded);
  useEffect(() => {
    elementRef.current = element;
    onDiscardedRef.current = onDiscarded;
  }, [element, onDiscarded]);

  useEffect(() => {
    if (!element) return;
    const onLoad = (event: Event) => {
      const id = draftIdFromEvent(event);
      if (id !== null) idRef.current = id;
    };
    element.addEventListener("load", onLoad);
    return () => element.removeEventListener("load", onLoad);
  }, [element]);

  const id = useCallback(() => idRef.current, []);
  const keep = useCallback(() => {
    if (keepingRef.current) return false;
    keepingRef.current = true;
    return true;
  }, []);
  const release = useCallback(() => {
    keepingRef.current = false;
  }, []);
  const discard = useCallback((): Promise<void> => {
    const draftId = idRef.current;
    if (draftId === null || keepingRef.current) return Promise.resolve();
    deletionRef.current ??= service.deleteRemoteTemplate(draftId).catch((error) => {
      deletionRef.current = null;
      throw error;
    });
    return deletionRef.current;
  }, [service]);

  // Route changes (links, Sidebar and Header navigate()) unmount the page. Closing the
  // tab can orphan a draft; that is accepted rather than intercepting unload.
  useEffect(
    () => () => {
      const mounted = elementRef.current;
      if (idRef.current === null && mounted && !keepingRef.current) {
        deleteDraftWhenItLoads(mounted, service);
      }
      // A draft that survives is orphaned on DocuSeal: the list shows only registered templates.
      void discard().then(
        () => onDiscardedRef.current?.(),
        () => {}
      );
    },
    [discard, service]
  );

  return { id, keep, release, discard };
}
